import {
  Address,
  LiquidityPool,
  LiquidityPosition,
  TICK_PRICE_BASE,
  WeiPerEther,
  Zero,
  feeTierToPercentage,
} from 'nftx.js';
import { useFormatter, useTranslations } from 'use-intl';
import Button from '../../elements/Button';
import VaultIcon from '../../elements/VaultIcon';
import FormattedEther from '../../elements/FormattedEther';
import Icon, { Icons } from '../../elements/Icon';
import LiquiditySvg from '../../elements/LiquiditySvg';
import { PriceRangeCard } from './elements';
import { normalizeTicks } from '@/domain/ticks/helpers';
import { useNetwork } from '@nftx/react';

export interface ManageLiquidityPositionProps {
  symbol: string;
  pool: LiquidityPool;
  position: LiquidityPosition;
  vaultId: string;
  tokenAddress: Address;
  onClaim: () => void;
  onWithdraw: () => void;
  onIncrease: () => void;
}

export const ManageLiquidityPosition = ({
  position: {
    inRange,
    tickLowerValue,
    tickUpperValue,
    vToken,
    eth,
    claimableEth,
    claimableVToken,
    tokenId,
    manager,
  },
  pool: { feeTier, name, tokens, tick },
  pool,
  symbol,
  vaultId,
  tokenAddress,
  onClaim,
  onWithdraw,
  onIncrease,
}: ManageLiquidityPositionProps) => {
  const t = useTranslations('ManageLiquidityPosition');
  const { number } = useFormatter();

  const isOpen = vToken > Zero || eth > Zero;

  const [tickLower, tickUpper] = normalizeTicks(
    tokenAddress,
    tickLowerValue,
    tickUpperValue,
    pool
  );
  return (
    <div className="p-6 bg-gray-50">
      <header className="flex items-center mt-2">
        <h1 className="font-black text-lg mr-4">
          {tokens.map((t) => t.symbol).join(' + ')}
        </h1>
        <span className="rounded-md p-2 bg-gray-200 border border-gray-300 text-xs leading-none mr-4">
          {number(feeTierToPercentage(feeTier), {
            style: 'percent',
            minimumFractionDigits: 1,
          })}
        </span>
        {inRange ? (
          <span className="text-green-600 text-xs">{t('inRange')}</span>
        ) : (
          <span className="text-red-500 text-xs">{t('outOfRange')}</span>
        )}
      </header>
      <section className="sm:flex my-4 items-start">
        <LiquiditySvg
          tokenId={tokenId}
          manager={manager}
          alt={name}
          className="hidden sm:block w-2/5 flex-none mr-4"
        />
        <div className="space-y-4 flex-1">
          <div className="bg-gray-100 border border-gray-300 rounded-2xl p-2">
            <header className="flex mb-4">
              <h3 className="text-lg font-normal p-2">{t('liquidity')}</h3>
              <aside className="ml-auto flex items-center space-x-4 px-2">
                {isOpen && (
                  <button
                    type="button"
                    className="text-xs text-pink-500 hover:underline"
                    onClick={onWithdraw}
                  >
                    {t('remove')}
                  </button>
                )}
                <Button size="small" onClick={onIncrease}>
                  {t('add')}
                </Button>
              </aside>
            </header>
            <dl className="p-4 rounded-lg border border-gray-300 bg-gray-50 space-y-2">
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <VaultIcon vaultId={vaultId} className="mr-4 w-5" />
                  {symbol}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther value={vToken} />
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <Icon name={Icons.ETH} className="mr-4" size={20} />
                  {'ETH'}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther value={eth} />
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-gray-100 border border-gray-300 rounded-2xl p-2">
            <header className="flex mb-4">
              <h3 className="text-lg font-normal p-2">{t('unclaimedFees')}</h3>
              {!!(claimableEth || claimableVToken) && (
                <aside className="ml-auto flex items-center space-x-4 px-2">
                  <Button size="small" onClick={onClaim}>
                    {t('claim')}
                  </Button>
                </aside>
              )}
            </header>
            <dl className="p-4 rounded-lg border border-gray-300 bg-gray-50 space-y-2">
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <VaultIcon vaultId={vaultId} className="mr-4 w-5" />
                  {symbol}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther value={claimableVToken} />
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="flex items-center font-bold">
                  <Icon name={Icons.ETH} className="mr-4" size={20} />
                  {'ETH'}
                </dt>
                <dd className="font-normal text-lg">
                  <FormattedEther value={claimableEth} />
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
      <footer className="bg-gray-100 border border-gray-300 p-4 rounded-2xl">
        <header className="flex items-center mb-4 flex-wrap">
          <h4 className="text-lg mr-4">{t('priceRange')}</h4>
          {inRange ? (
            <span className="text-green-600 text-xs">{t('inRange')}</span>
          ) : (
            <span className="text-red-500 text-xs">{t('outOfRange')}</span>
          )}
          <span className="ml-auto text-sm font-medium">
            {t('currentPrice', {
              price: number(TICK_PRICE_BASE ** Number(tick), {
                maximumFractionDigits: 4,
              }),
              symbol,
            })}
          </span>
        </header>
        <div className="flex space-x-4 items-center">
          <PriceRangeCard
            title={t('minPrice')}
            tick={tickLower}
            subtitle={t('ratio', { symbol })}
          />
          <div className="w-8 text-center">
            <Icon
              name={Icons.ARROWS_RIGHT_LEFT}
              className="text=gray-400 inline-flex"
            />
          </div>
          <PriceRangeCard
            title={t('maxPrice')}
            tick={tickUpper}
            subtitle={t('ratio', { symbol })}
          />
        </div>
      </footer>
    </div>
  );
};

export default ManageLiquidityPosition;
